import { startRequestScopeSync } from '#~/api/request-scope-sync.js';
import { BootBudgetGate } from '#~/routes/boot-overlay/boot-budget-view.js';
/* eslint-disable import/no-internal-modules */
import '#styles/index.css';
import { Provider as AuthProvider } from '#~/auth/index.js';
import { AnalyticsProvider } from '#~/analytics/index.js';
import { Provider as CommandProvider } from '#~/command/provider/index.js';
import { Provider as QueryProvider } from '#~/react-query/provider.js';
import { Paths } from '#~/routes/index.js';
import { Router } from '#~/routes/router.jsx';
import { Provider as DialogProvider } from '@studio/design-system/dialog';
import { Provider as PortalProvider } from '@studio/design-system/portal';
import T from '@studio/design-system/themes';
import { getCurrentVendor, getCurrentVendorLogo } from '@studio/design-system/themes/vendor.js';
import type { ReactElement } from 'react';
import { Boundary } from './boundary/boundary.js';
import { HeadMetadata } from './head-metadata.js';
import { Trackers } from './trackers/trackers.js';
import type { Props } from './types.js';

// Prevent cyclic references
import { BrowserRouter } from 'react-router-dom';
import { twMerge } from 'cn';

// There imoprts are needed here in order to prevent cyclic references, as the ones above
import { config } from '#~/config/config.js';
import {
  PostHogManager,
  recordAnalyticsStartFailure,
  resolveFeatureFlagRefreshConfig
} from '#~/posthog/index.js';
import { isElectronTrayWindow } from '#~/routes/electron/tray/utils.js';
import type { PostHog } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { logger } from '#~/logger/logger.js';
import { useEffect } from 'react';
import { markIntercomEditorActive, useIntercomEditorActive } from '@studio/design-system/dialog';
import { SetCorrelationProvider } from '@tryiris-ai/mailroom-helper/backend';
// Spec 4 (D1): app-shell commit marker + the analytics bridge for the
// per-document startup recorder. The recorder itself has no app imports; this
// component is the only place the two meet.
import { advanceRendererDocumentPhase, markRendererMilestone } from '#~/boot/renderer-startup.js';
import { RendererStartupAnalytics } from '#~/boot/renderer-startup-analytics.jsx';

const vendor = getCurrentVendor();

const Titles = {
  ptzoptics: 'Hive',
  iris: 'Iris Studio'
};

const Colors = {
  ptzoptics: {
    tileColor: '#101011',
    themeColor: '#BD6100'
  },
  iris: {
    tileColor: '#10131d',
    themeColor: '#3664ff'
  }
};

const title = Titles[vendor];

const headLinks = [
  {
    key: 'apple-touch-icon',
    rel: 'apple-touch-icon',
    sizes: '180x180',
    href: `/assets/favicon/${vendor}/apple-touch-icon.png`
  },
  {
    key: 'favicon-32',
    rel: 'icon',
    type: 'image/png',
    sizes: '32x32',
    href: `/assets/favicon/${vendor}/favicon-32x32.png`
  },
  {
    key: 'favicon-16',
    rel: 'icon',
    type: 'image/png',
    sizes: '16x16',
    href: `/assets/favicon/${vendor}/favicon-16x16.png`
  },
  {
    key: 'manifest',
    rel: 'manifest',
    href: `/assets/favicon/${vendor}/site.webmanifest`
  },
  {
    key: 'shortcut-icon',
    rel: 'shortcut icon',
    href: `/assets/favicon/${vendor}/favicon.ico`
  },
  {
    key: 'vendor-logo-preload',
    rel: 'preload',
    href: getCurrentVendorLogo(),
    as: 'image'
  }
];

const headMeta = [
  {
    key: 'msapplication-config',
    name: 'msapplication-config',
    content: `/assets/favicon/${vendor}/browserconfig.xml`
  },
  {
    key: 'msapplication-TileColor',
    name: 'msapplication-TileColor',
    content: Colors[vendor].tileColor
  },
  {
    key: 'theme-color',
    name: 'theme-color',
    content: Colors[vendor].themeColor
  }
];

// Initialize PostHog client at module load time
const initializePostHog = (): PostHog | undefined => {
  if (isElectronTrayWindow()) {
    return undefined;
  }

  if (!config.posthogApiKey || !config.posthogApiHost) {
    // Product contract: PostHog-controlled capabilities fail OFF when the
    // service is unavailable, but Studio itself remains operational.
    logger.error('[Root] PostHog configuration is unavailable; capabilities remain disabled');
    return undefined;
  }

  // Downloadable CI builds suppress telemetry, but human retests still need
  // live capabilities. Only automated E2E uses deterministic local flags.
  // Do not persist consent changes that could mute a later live installer.
  // Per rule: reference-rules-in-comments (ai.knowledge/global).
  const isAutomatedTest = window.electron?.process?.env?.IRIS_E2E_TEST === '1';
  const { posthogApiKey, posthogApiHost } = config;
  // One closure for the first attempt AND the ENG-4873 web retry, so a retry
  // can never start PostHog with different options than the launch did.
  const startPostHog = (): PostHog =>
    PostHogManager.getInstance().initializeSync(
      posthogApiKey,
      {
        api_host: posthogApiHost,
        ui_host: 'https://us.posthog.com',
        disable_session_recording: config.posthogCaptureDisabled,
        ...(config.posthogCaptureDisabled
          ? {
              advanced_disable_feature_flags: isAutomatedTest,
              opt_out_capturing_by_default: true,
              opt_out_persistence_by_default: false,
              // Persisted opt-in overrides the SDK's default consent. The
              // capture boundary must stay closed even for returning users.
              before_send: () => null
            }
          : {})
      },
      // Manual off builds use the itg project and its foreground recovery,
      // while automated off runs must remain network-free for flags.
      resolveFeatureFlagRefreshConfig(
        config.posthogEnv === 'off' && !isAutomatedTest ? 'itg' : config.posthogEnv
      )
    );

  try {
    const client = startPostHog();

    if (config.env !== 'prod' && false) {
      PostHogManager.getInstance().enableDebug();
    }

    return client;
  } catch (error) {
    logger.error('[Root] Failed to initialize PostHog; capabilities remain disabled', error);
    // ENG-4873 (N2): PostHog did not start, so its own error tracking cannot see
    // this. Record the failure where it can be traced (desktop session log; web
    // console plus one retry). Nothing is shown to the person.
    recordAnalyticsStartFailure({ error, retry: startPostHog });
    return undefined;
  }
};

const posthogClient = initializePostHog();

startRequestScopeSync();

SetCorrelationProvider(() => PostHogManager.getInstance().getLambdaCorrelationContext());

const IntercomEditorPointerEventsGuard = (): null => {
  const isIntercomEditorActive = useIntercomEditorActive();

  useEffect(() => {
    if (!isIntercomEditorActive || typeof document === 'undefined') {
      return;
    }

    const { body } = document;
    const previousPointerEvents = body.style.getPropertyValue('pointer-events');
    const previousPriority = body.style.getPropertyPriority('pointer-events');
    // Per rule: stay-in-scope (ai.knowledge/workflow) — scope the PR #23 CSS escape hatch to
    // the Intercom editor only so the normal Messenger keeps Intercom's own positioning.
    const restoreEditorMarker = markIntercomEditorActive(body);

    const forceAutoPointerEvents = () => {
      body.style.setProperty('pointer-events', 'auto', 'important');
    };

    forceAutoPointerEvents();

    const observer = new MutationObserver(() => {
      if (
        body.style.getPropertyValue('pointer-events') !== 'auto' ||
        body.style.getPropertyPriority('pointer-events') !== 'important'
      ) {
        forceAutoPointerEvents();
      }
    });

    observer.observe(body, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => {
      observer.disconnect();

      restoreEditorMarker();

      if (previousPointerEvents) {
        body.style.setProperty('pointer-events', previousPointerEvents, previousPriority);
      } else {
        body.style.removeProperty('pointer-events');
      }
    };
  }, [isIntercomEditorActive]);

  return null;
};

/**
 * Spec 4 (D1): records the first commit of the app shell and advances the
 * disjoint document partition to `until-overlay-ready`.
 *
 * A COMMIT marker, not a paint marker: React has committed `#studio` to the DOM
 * here, which the browser has not necessarily painted yet. The recorder's
 * first-occurrence guard makes the StrictMode double-invoke a no-op, and the
 * phase advance is forward-only, so a remount cannot rewind the partition.
 */
const ShellCommitMarker = (): null => {
  useEffect(() => {
    markRendererMilestone('app-shell-committed');
    advanceRendererDocumentPhase('until-overlay-ready');
  }, []);

  return null;
};

/** Top-level application component. */
export const Root = ({ className, theme: _theme, ...props }: Props): ReactElement => {
  return (
    <div
      className={twMerge('studio grid min-h-screen', className)}
      data-theme={T.getTheme()}
      id='studio'
      {...props}
    >
      <Boundary>
        <HeadMetadata links={headLinks} meta={headMeta} title={title} />
        <BootBudgetGate>
          <BrowserRouter>
            <QueryProvider>
              <AuthProvider callbackUrl={`/${Paths.oauth2.callback}`}>
                {posthogClient ? (
                  <PostHogProvider client={posthogClient}>
                    <AnalyticsProvider>
                      <ShellCommitMarker />
                      <RendererStartupAnalytics />
                      <Trackers>
                        <IntercomEditorPointerEventsGuard />
                        <PortalProvider>
                          <DialogProvider>
                            <CommandProvider>
                              <Router />
                            </CommandProvider>
                          </DialogProvider>
                        </PortalProvider>
                      </Trackers>
                    </AnalyticsProvider>
                  </PostHogProvider>
                ) : (
                  <AnalyticsProvider>
                    <ShellCommitMarker />
                    <RendererStartupAnalytics />
                    <Trackers>
                      <IntercomEditorPointerEventsGuard />
                      <PortalProvider>
                        <DialogProvider>
                          <CommandProvider>
                            <Router />
                          </CommandProvider>
                        </DialogProvider>
                      </PortalProvider>
                    </Trackers>
                  </AnalyticsProvider>
                )}
              </AuthProvider>
            </QueryProvider>
          </BrowserRouter>
        </BootBudgetGate>
      </Boundary>
    </div>
  );
};
